import React, { useCallback, useEffect, useState } from 'react';
import { Database, Loader2, RefreshCw } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import { useAuth } from '../../contexts/AuthContext';
import {
  getProjectCrmAccess,
  ProjectCrmAccess,
  ProjectCrmIdentity,
  resyncProjectCrmAccess,
} from '../../services/api';

/**
 * crm-per-project-scoping: who can open this project's CRM records, and how.
 * Visible to the project owner and project admins only (the route 403/404s
 * otherwise, in which case this panel renders nothing).
 */
interface Props {
  projectId: string;
}

const STATUS_TEXT: Record<string, string> = {
  active: 'Active',
  provisioning: 'Setting up…',
  revoking: 'Removing…',
  revoked: 'Removed',
  member_not_migrated: "Waiting for this member's CRM upgrade",
  not_provisioned: 'Not set up yet',
};

const ERROR_TEXT: Record<string, string> = {
  crm_unreachable: 'CRM unreachable',
  role_assign_failed: 'role could not be set',
  group_assign_failed: 'group could not be set',
  verify_failed: 'verification failed',
  decrypt_failed: 'credentials unreadable',
  duplicate_username: 'duplicate CRM user',
  reassign_list_failed: 'record reassignment failed',
  reassign_incomplete: 'record reassignment incomplete',
};

export const statusLabel = (row: Pick<ProjectCrmIdentity, 'status' | 'last_error_short'>): string => {
  if (row.status === 'failed') {
    return `Failed: ${(row.last_error_short && ERROR_TEXT[row.last_error_short]) || 'unknown error'}`;
  }
  return STATUS_TEXT[row.status] || row.status;
};

const viaLabel = (row: ProjectCrmIdentity): string => {
  if (row.granted_via === 'owner') return 'Owner';
  if (row.granted_via === 'project_member') return 'Project member';
  return `Team: ${row.team_name || 'unnamed'}`;
};

const accessLabel = (kind: ProjectCrmIdentity['access_kind']): string =>
  kind === 'rw' ? 'Read & write' : kind === 'ro' ? 'Read only' : '—';

const ProjectCrmAccessPanel: React.FC<Props> = ({ projectId }) => {
  const { user } = useAuth();
  const [data, setData] = useState<ProjectCrmAccess | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getProjectCrmAccess(projectId));
      setHidden(false);
    } catch (e: any) {
      const st = e?.response?.status;
      if (st === 403 || st === 404 || st === 401) setHidden(true);
      else setMessage('Could not load CRM access.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  const isOwner = !!data?.identities.some(r => r.granted_via === 'owner' && r.user_id === user?.user_id);

  const handleResync = async () => {
    setResyncing(true);
    setMessage(null);
    try {
      await resyncProjectCrmAccess(projectId);
      setMessage('Resync queued. Changes appear within a minute.');
      setTimeout(load, 3000);
    } catch (e: any) {
      setMessage(e?.response?.status === 429
        ? 'Resync was just requested — please wait a minute.'
        : 'Could not queue a resync.');
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div className="ps-crm-access" style={{ marginTop: 20 }}>
      <div className="ps-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Database size={18} />
        <h4 style={{ margin: 0, color: 'var(--text)' }}>CRM access</h4>
      </div>
      <p className="ps-description" style={{ color: 'var(--text-secondary)' }}>
        Anyone this project is shared with can open its CRM records. View access is read-only; Edit or Admin
        access can change records.
      </p>

      {loading ? (
        <div className="ps-loading" style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text-secondary)' }}>
          <Loader2 size={16} className="ps-spin" /> Loading CRM access…
        </div>
      ) : data && data.identities.length > 0 ? (
        <div style={{ overflowX: 'auto', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', background: 'var(--panel-strong)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                {['Member', 'Granted via', 'CRM access', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--stroke)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.identities.map(r => (
                <tr key={`${r.user_id}-${r.access_kind}-${r.status}`}>
                  <td style={{ padding: '8px 10px', color: 'var(--text)', borderBottom: '1px solid var(--stroke)' }}>
                    {r.full_name || r.email || r.user_id.slice(0, 8)}
                    {r.full_name && r.email && (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{r.email}</div>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--text)', borderBottom: '1px solid var(--stroke)' }}>{viaLabel(r)}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--text)', borderBottom: '1px solid var(--stroke)' }}>{accessLabel(r.access_kind)}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--text)', borderBottom: '1px solid var(--stroke)' }}>{statusLabel(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="ps-empty-state" style={{ color: 'var(--text-secondary)' }}>
          {data && data.scoping === false
            ? "This project's CRM isn't split per project yet — your CRM shows your whole workspace."
            : 'No one else has CRM access to this project.'}
        </p>
      )}

      {message && <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '8px 0 0' }}>{message}</p>}

      {isOwner && (
        <div style={{ marginTop: 12 }}>
          <GlassButton
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={handleResync}
            loading={resyncing}
            disabled={resyncing}
          >
            Resync CRM access
          </GlassButton>
        </div>
      )}
    </div>
  );
};

export default ProjectCrmAccessPanel;
